import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PencilIcon, TrashBinIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon } from '@/icons';
import { RowActionsMenu } from '@/components/admin/RowActionsMenu';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { DraggableRow, DragHandle } from '@/components/cccd/DraggableRow';
import { useDragReorder } from '@/hooks/useDragReorder';
import { cccdAdmin, type EventCategory, type SeedbedOption } from '@/lib/api/cccdAdminClient';
import { applyOptimistic, rollbackOptimistic } from '@/lib/optimisticMutation';
import { showUndoToast } from '@/lib/undoToast';
import { toastError } from '@/lib/toast';
import { StatusPill } from '@/components/cccd/StatusPill';
import { LoadState } from "@/components/ui/LoadState"

const ITEM_TYPE = 'cccd-category-row';

export type CategoryParent = { type: 'event'; eventId: string } | { type: 'regional'; regionalId: string };

// Reused unmodified by RegionalShell's Configuración tab (cccd-ux-redesign),
// parameterized by parent instead of duplicated per entity — mirrors the
// {type,id} discriminator PodiumResultsSection already used for event vs.
// regional results. Does NOT wrap its table in its own <DndProvider> — this
// section always renders alongside EventPlansSection (also drag-reorderable)
// in the same Configuración tab, and react-dnd's HTML5Backend throws
// "Cannot have two HTML5 backends at the same time" if two DndProviders
// mount concurrently. The caller (EventDetailPage / RegionalDetailPage)
// wraps both sections in one shared <DndProvider>.
export function EventCategoriesSection({ parent }: { parent: CategoryParent }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const parentId = parent.type === 'event' ? parent.eventId : parent.regionalId;
  const basePath = parent.type === 'event' ? `/cccd/events/${parentId}` : `/cccd/regionals/${parentId}`;
  const queryKey = ['event-categories', parent.type, parentId];

  const { data: categories = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => (parent.type === 'event' ? cccdAdmin.getEventCategories(parentId) : cccdAdmin.getRegionalCategories(parentId)),
  });

  // El listado público de categorías no trae el nombre del semillero (solo
  // el id) para no exponerlo fuera del panel — se resuelve aquí contra el
  // catálogo de semilleros que ya usa CategoryFormPage.
  const { data: seedbedOptions = [] } = useQuery<SeedbedOption[]>({
    queryKey: ['seedbed-options'],
    queryFn: () => cccdAdmin.listSeedbedOptions(),
  });
  const seedbedName = (id: string | null) => seedbedOptions.find((sb) => sb.id === id)?.name;

  const { moveRow, onDragStart, onDrop, moveBy } = useDragReorder<EventCategory>(
    queryKey,
    (categoryId, orderIndex) =>
      parent.type === 'event'
        ? cccdAdmin.updateEventCategory(parentId, categoryId, { orderIndex })
        : cccdAdmin.updateRegionalCategory(parentId, categoryId, { orderIndex }),
  );

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      parent.type === 'event' ? cccdAdmin.deleteEventCategory(parentId, id) : cccdAdmin.deleteRegionalCategory(parentId, id),
  });

  // Elimina de inmediato en la UI y difiere el DELETE real hasta que expire
  // la ventana de "Deshacer" — ver design.md, receta "CRUD de lista".
  const handleDelete = async (cat: EventCategory) => {
    const previous = await applyOptimistic<EventCategory[]>(qc, queryKey, (prev) =>
      (prev ?? []).filter((c) => c.id !== cat.id),
    );
    showUndoToast({
      message: `Categoría "${cat.name}" eliminada`,
      onUndo: () => rollbackOptimistic(qc, queryKey, previous),
      onCommit: async () => {
        try {
          await deleteMutation.mutateAsync(cat.id);
          invalidate();
        } catch {
          rollbackOptimistic(qc, queryKey, previous);
          toastError('No se pudo eliminar la categoría.');
        }
      },
    });
  };

  return (
    <SectionHeader
      title="Categorías"
      subtitle="Arrastra para reordenar — el orden define cómo se muestran al equipo"
      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>}
    >
      <button
        type="button"
        onClick={() => navigate(`${basePath}/categories/new`)}
        className="flex w-full items-center gap-2.5 rounded-[14px] border-[1.5px] border-dashed border-gray-300 bg-gray-25 min-h-11 px-3.5 py-3.5 text-gray-500 transition-colors hover:border-brand-300 hover:text-brand-500 dark:border-gray-700 dark:bg-white/[0.02] dark:hover:border-brand-700"
      >
        <PlusIcon className="size-4" />
        <span className="text-[13px] font-medium">Agregar categoría nueva</span>
      </button>

      {isLoading ? (
        <LoadState />
      ) : (
        <div className="-mx-6 mt-4 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr>
              {['', 'Categoría', 'Precio', 'Semillero', 'Estado', ''].map((h, i) => (
                <th key={i} className="border-t border-gray-100 px-6 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 dark:border-gray-800">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {categories.map((cat, index) => (
              <DraggableRow key={cat.id} id={cat.id} index={index} itemType={ITEM_TYPE} moveRow={moveRow} onDragStart={onDragStart} onDrop={onDrop}>
                <td className="w-6 py-3.5 pl-6"><DragHandle /></td>
                <td className="px-2 py-3.5 font-medium text-gray-800 dark:text-white/90">{cat.name}</td>
                <td className="px-2 py-3.5">
                  <span className="flex items-center gap-3 text-[13px] text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1.5"><span className="size-1.5 rounded-full bg-warning-500" />${cat.basePriceCop.toLocaleString('es-CO')}</span>
                    <span className="flex items-center gap-1.5"><span className="size-1.5 rounded-full bg-brand-500" />${cat.basePriceUsd?.toLocaleString('en-US') ?? '—'}</span>
                  </span>
                </td>
                <td className="px-2 py-3.5">
                  {seedbedName(cat.seedbedId) ? (
                    <span className="text-[13px] text-gray-600 dark:text-gray-400">{seedbedName(cat.seedbedId)}</span>
                  ) : (
                    <span className="text-[13px] text-gray-500 dark:text-gray-400">Sin semillero</span>
                  )}
                </td>
                <td className="px-2 py-3.5">
                  <StatusPill tone={cat.isActive ? 'success' : 'gray'} label={cat.isActive ? 'Activa' : 'Inactiva'} />
                </td>
                <td className="py-3.5 pr-6 text-right">
                  <RowActionsMenu
                    actions={[
                      {
                        label: 'Editar',
                        icon: <PencilIcon />,
                        to: `${basePath}/categories/${cat.id}/edit`,
                      },
                      // El arrastre no funciona al tacto ni con teclado: estas acciones reordenan igual.
                      { label: 'Subir', icon: <ArrowUpIcon />, onClick: () => void moveBy(index, -1), disabled: index === 0 },
                      { label: 'Bajar', icon: <ArrowDownIcon />, onClick: () => void moveBy(index, 1), disabled: index === categories.length - 1 },
                      {
                        label: 'Eliminar',
                        icon: <TrashBinIcon />,
                        onClick: () => handleDelete(cat),
                        variant: 'danger',
                      },
                    ]}
                  />
                </td>
              </DraggableRow>
            ))}
            {categories.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No hay categorías. Añade una para comenzar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      )}
    </SectionHeader>
  );
}
